/**
 * Notification API —— 用户收件箱 + 偏好 + 主题（/api/v1/notifications/*）。
 *
 * 后端为 joint-common notification 模块（snake_case wire 格式），复用
 * ./index 的 request()（超时 / Bearer / 401 统一登出 / 错误信封），本文件
 * 负责三件事：换前缀、snake→camel 映射、暴露类型化信封。SSE 流的连接管理
 * 不在此层（见 hooks/useNotifications.ts 的 useNotificationStream）。
 */

import { request } from "./index";
import type {
  NotificationDigestFrequency,
  NotificationInboxItem,
  NotificationInboxMeta,
  NotificationPreferenceItem,
  NotificationPreferences,
  NotificationTopic,
} from "../types/notification";

const NOTIFICATION_PREFIX = "/api/v1/notifications";

// --- wire 格式（snake_case，逐字段对照 router.py 的 ResponseModel） ---

interface InboxItemWire {
  id: string;
  title: string;
  body: string;
  is_read: boolean;
  read_at: string | null;
  category: string | null;
  ref_type: string | null;
  ref_id: string | null;
  created_at: string;
}

interface InboxMetaWire {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

interface TopicWire {
  id: string;
  code: string;
  name: string;
  description: string;
  default_priority: string;
  is_active: boolean;
}

interface PreferenceItemWire {
  topic_code: string;
  channel: string;
  enabled: boolean;
}

interface PreferencesWire {
  preferences: PreferenceItemWire[];
  digest_frequency: NotificationDigestFrequency;
}

// --- 映射 ---

function mapInboxItem(raw: InboxItemWire): NotificationInboxItem {
  return {
    id: raw.id,
    title: raw.title,
    body: raw.body,
    isRead: raw.is_read,
    readAt: raw.read_at,
    category: raw.category,
    refType: raw.ref_type,
    refId: raw.ref_id,
    createdAt: raw.created_at,
  };
}

function mapTopic(raw: TopicWire): NotificationTopic {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    description: raw.description,
    defaultPriority: raw.default_priority,
    isActive: raw.is_active,
  };
}

function mapPreferenceItem(raw: PreferenceItemWire): NotificationPreferenceItem {
  return { topicCode: raw.topic_code, channel: raw.channel, enabled: raw.enabled };
}

/** 收件箱分页响应（camelCase 化）。 */
export interface NotificationInboxPage {
  success: boolean;
  data: NotificationInboxItem[];
  meta: NotificationInboxMeta;
}

export const notificationsApi = {
  /** GET /inbox — 当前用户收件箱，最新在前。 */
  listInbox: (params?: { page?: number; pageSize?: number }): Promise<NotificationInboxPage> => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    return request<{
      success: boolean;
      data: InboxItemWire[];
      meta: InboxMetaWire;
    }>(`/inbox?${qs}`, undefined, NOTIFICATION_PREFIX).then((res) => ({
      success: res.success,
      data: res.data.map(mapInboxItem),
      meta: {
        page: res.meta.page,
        pageSize: res.meta.page_size,
        total: res.meta.total,
        totalPages: res.meta.total_pages,
      },
    }));
  },

  /** PUT /inbox/{id}/read — 单条已读，返回已读后的行。 */
  markRead: (inboxId: string): Promise<{ success: boolean; data: NotificationInboxItem }> =>
    request<{ success: boolean; data: InboxItemWire }>(
      `/inbox/${inboxId}/read`,
      { method: "PUT" },
      NOTIFICATION_PREFIX,
    ).then((res) => ({ success: res.success, data: mapInboxItem(res.data) })),

  /** PUT /inbox/read-all — 全部已读，返回更新条数。 */
  markAllRead: (): Promise<{ success: boolean; data: { updatedCount: number } }> =>
    request<{ success: boolean; data: { updated_count: number } }>(
      "/inbox/read-all",
      { method: "PUT" },
      NOTIFICATION_PREFIX,
    ).then((res) => ({ success: res.success, data: { updatedCount: res.data.updated_count } })),

  /** GET /inbox/unread-count — 未读数（角标）。 */
  getUnreadCount: (): Promise<{ success: boolean; data: { unreadCount: number } }> =>
    request<{ success: boolean; data: { unread_count: number } }>(
      "/inbox/unread-count",
      undefined,
      NOTIFICATION_PREFIX,
    ).then((res) => ({ success: res.success, data: { unreadCount: res.data.unread_count } })),

  /** GET /topics — 激活主题清单（偏好界面的数据源）。 */
  listTopics: (): Promise<{ success: boolean; data: NotificationTopic[] }> =>
    request<{ success: boolean; data: TopicWire[] }>("/topics", undefined, NOTIFICATION_PREFIX).then(
      (res) => ({ success: res.success, data: res.data.map(mapTopic) }),
    ),

  /** GET /preferences — 当前用户偏好 + P3 摘要节奏（无行 = 默认订阅）。 */
  getPreferences: (): Promise<{ success: boolean; data: NotificationPreferences }> =>
    request<{ success: boolean; data: PreferencesWire }>(
      "/preferences",
      undefined,
      NOTIFICATION_PREFIX,
    ).then((res) => ({
      success: res.success,
      data: {
        preferences: res.data.preferences.map(mapPreferenceItem),
        digestFrequency: res.data.digest_frequency,
      },
    })),

  /** PUT /preferences — 写一条偏好（channel="" 为主题级开关）。 */
  setPreference: (args: {
    topicCode: string;
    channel: string;
    enabled: boolean;
  }): Promise<{ success: boolean; data: NotificationPreferenceItem }> =>
    request<{ success: boolean; data: PreferenceItemWire }>(
      "/preferences",
      {
        method: "PUT",
        body: JSON.stringify({
          topic_code: args.topicCode,
          channel: args.channel,
          enabled: args.enabled,
        }),
      },
      NOTIFICATION_PREFIX,
    ).then((res) => ({ success: res.success, data: mapPreferenceItem(res.data) })),

  /** PUT /preferences/digest — 设置 P3 摘要节奏。 */
  setDigestFrequency: (
    frequency: NotificationDigestFrequency,
  ): Promise<{ success: boolean; data: { digestFrequency: NotificationDigestFrequency } }> =>
    request<{ success: boolean; data: { digest_frequency: NotificationDigestFrequency } }>(
      "/preferences/digest",
      { method: "PUT", body: JSON.stringify({ frequency }) },
      NOTIFICATION_PREFIX,
    ).then((res) => ({
      success: res.success,
      data: { digestFrequency: res.data.digest_frequency },
    })),
};
